import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { buildHelpSections } from './helpContent';
import useCases from './useCases';
import './HelpPanel.css';

function HelpPanel({ isOpen, onClose, activeSection, onLoadUseCase, onSetCycleRange, features = null, schemaConfig = null }) {
  const [activeTab, setActiveTab] = useState('guide'); // 'guide' | 'usecases'
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [expandedFullText, setExpandedFullText] = useState(new Set());
  const sectionRefs = useRef({});
  const panelRef = useRef(null);

  // Build the manual for the active deployment: only panels that exist in this
  // build appear, and terminology follows the active schema's labels.
  const helpSections = useMemo(() => buildHelpSections({
    features,
    labels: schemaConfig?.labels || {},
    schemaKey: schemaConfig?.key || '',
    facets: schemaConfig?.facets || null,
    networkMeta: schemaConfig?.network || null,
  }), [features, schemaConfig]);

  // Use cases are MaxIV-specific; only show the tab for that schema.
  // features === null means we haven't loaded config yet — keep it hidden until known.
  const schemaKey = schemaConfig?.key || '';
  const useCasesAvailable = schemaKey === 'maxiv';

  // Never leave the user stranded on a hidden tab.
  useEffect(() => {
    if (!useCasesAvailable && activeTab === 'usecases') setActiveTab('guide');
  }, [useCasesAvailable, activeTab]);

  // When activeSection changes (from contextual ⓘ click), collapse all others and show only that section
  useEffect(() => {
    if (activeSection && isOpen) {
      setActiveTab('guide');
      // Collapse everything, then expand only the target section
      setExpandedSections(new Set([activeSection]));
      setExpandedFullText(new Set());
      // Scroll to it after a brief delay for animation
      setTimeout(() => {
        const el = sectionRefs.current[activeSection];
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 350);
    }
  }, [activeSection, isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const toggleSection = useCallback((sectionId) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  const toggleFullText = useCallback((sectionId) => {
    setExpandedFullText(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedSections(new Set(helpSections.map(s => s.id)));
  }, [helpSections]);

  const collapseAll = useCallback(() => {
    setExpandedSections(new Set());
    setExpandedFullText(new Set());
  }, []);

  const handleUseCaseAction = useCallback((useCase) => {
    if (useCase.actionType === 'applyFilters' && useCase.filterPreset) {
      onLoadUseCase(useCase.filterPreset, { connectionStrength: useCase.connectionStrength });
      onClose();
    } else if (useCase.actionType === 'setCycleRange') {
      if (onSetCycleRange) onSetCycleRange();
      onClose();
    }
  }, [onLoadUseCase, onSetCycleRange, onClose]);

  // Pop out guide into a standalone browser window
  const handlePopOut = useCallback(() => {
    const sections = helpSections.map((s, i) =>
      `<div class="section">
        <h2><span class="num">${i}</span>${s.title}</h2>
        ${s.full.map(t => `<p>${t}</p>`).join('')}
      </div>`
    ).join('');

    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>nexus — extended manual</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'DIN 2014',system-ui,sans-serif;background:#faf8f5;color:#3a3630;padding:2rem 3rem;max-width:820px;margin:0 auto;line-height:1.65}
  h1{font-size:1.5rem;font-weight:700;margin-bottom:.15rem;color:#1c566b}
  h1 span{font-weight:400;font-size:1rem;color:#888;margin-left:.5rem}
  .intro{font-size:.78rem;color:#999;margin-bottom:1.5rem}
  .section{margin-bottom:1.75rem;padding:1rem;border-radius:8px;background:#fff;border:1px solid #e8e3da}
  .section h2{font-size:.95rem;font-weight:700;color:#1c566b;margin-bottom:.6rem;display:flex;align-items:center;gap:.5rem}
  .section .num{background:rgba(28,86,107,.08);color:#1c566b;width:22px;height:22px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-size:.7rem;font-weight:700;flex-shrink:0}
  .section p{font-size:.82rem;margin-bottom:.5rem;color:#555}
  .footer{margin-top:2rem;font-size:.65rem;color:#aaa;text-align:center;border-top:1px solid #e0d8cc;padding-top:.75rem}
</style></head><body>
  <h1>nexus <span>extended manual (beta_v3.2.0)</span></h1>
  <p class="intro">Full reference guide — keep this window open while you work in the dashboard.</p>
  ${sections}
  <div class="footer">nexus extended manual · Feb 2026 · Support: hej@tilleralpha.com</div>
</body></html>`;

    const w = window.open('', '_blank', 'width=860,height=900,scrollbars=yes');
    if (w) {
      w.document.write(html);
      w.document.close();
    }
  }, [helpSections]);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div className="help-panel-backdrop" onClick={onClose} />
      )}

      {/* Slide-in panel */}
      <div className={`help-panel${isOpen ? ' open' : ''}`} ref={panelRef}>
        {/* Header */}
        <div className="help-panel-header">
          <div className="help-panel-title">
            <span className="help-panel-brand">nexus</span>
            <span className="help-panel-subtitle">manual</span>
          </div>
          <div className="help-panel-header-actions">
            <button
              className="help-panel-popout"
              onClick={handlePopOut}
              title="Open extended manual in new window"
            >
              ⧉ <span className="help-popout-label">Extended manual</span>
            </button>
            <button className="help-panel-close" onClick={onClose} title="Close">
              ×
            </button>
          </div>
        </div>

        {/* Tabs */}
        {useCasesAvailable && (
        <div className="help-panel-tabs">
          <button
            className={`help-tab-btn${activeTab === 'guide' ? ' active' : ''}`}
            onClick={() => setActiveTab('guide')}
          >
            Guide
          </button>
          <button
            className={`help-tab-btn${activeTab === 'usecases' ? ' active' : ''}`}
            onClick={() => setActiveTab('usecases')}
          >
            Use Cases
          </button>
        </div>
        )}

        {/* Content */}
        <div className="help-panel-content">
          {activeTab === 'guide' && (
            <div className="help-guide">
              {/* Expand/Collapse all */}
              <div className="help-guide-controls">
                <button className="help-expand-btn" onClick={expandAll} title="Expand all sections">
                  Expand All
                </button>
                <button className="help-expand-btn" onClick={collapseAll} title="Collapse all sections">
                  Collapse All
                </button>
              </div>

              {/* Sections accordion */}
              {helpSections.map((section, index) => {
                const isExpanded = expandedSections.has(section.id);
                const showFull = expandedFullText.has(section.id);
                const isHighlighted = activeSection === section.id;

                return (
                  <div
                    key={section.id}
                    className={`help-section${isHighlighted ? ' highlighted' : ''}`}
                    ref={el => sectionRefs.current[section.id] = el}
                  >
                    <button
                      className="help-section-header"
                      onClick={() => toggleSection(section.id)}
                    >
                      <span className="help-section-number">{index}</span>
                      <span className="help-section-title">{section.title}</span>
                      <span className={`help-section-chevron${isExpanded ? ' expanded' : ''}`}>
                        ›
                      </span>
                    </button>

                    {isExpanded && (
                      <div className="help-section-body">
                        {/* Show short content */}
                        {section.short.map((text, i) => (
                          <p key={i} className="help-text">{text}</p>
                        ))}

                        {/* Toggle for full content */}
                        {section.full.length > section.short.length && (
                          <>
                            {showFull && (
                              <div className="help-full-content">
                                <div className="help-full-divider" />
                                {section.full
                                  .filter((text) => !section.short.includes(text))
                                  .map((text, i) => (
                                    <p key={i} className="help-text help-text-full">{text}</p>
                                  ))}
                              </div>
                            )}
                            <button
                              className="help-read-more"
                              onClick={() => toggleFullText(section.id)}
                            >
                              {showFull ? '← Show less' : 'Read more →'}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'usecases' && useCasesAvailable && (
            <div className="help-usecases">
              <p className="help-usecases-intro">
                These examples demonstrate how to use nexus for real analytical scenarios at MAX IV. 
                Where available, you can load the filter configuration directly.
              </p>

              {useCases.map((uc) => (
                <div key={uc.id} className="help-usecase-card">
                  <div className="help-usecase-header">
                    <h3 className="help-usecase-title">{uc.title}</h3>
                    <span className="help-usecase-subtitle">{uc.subtitle}</span>
                  </div>

                  <p className="help-usecase-description">{uc.description}</p>

                  <div className="help-usecase-steps">
                    <span className="help-steps-label">Steps</span>
                    <ol>
                      {uc.steps.map((step, i) => (
                        <li key={i}>{step}</li>
                      ))}
                    </ol>
                  </div>

                  {(uc.filterPreset || uc.actionType === 'setCycleRange') && (
                    <button
                      className="help-usecase-load-btn"
                      onClick={() => handleUseCaseAction(uc)}
                    >
                      {uc.actionLabel}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="help-panel-footer">
          <span>nexus manual (beta_v3.2.0) · Feb 2026</span>
          <span className="help-footer-support">Support: hej@tilleralpha.com</span>
        </div>
      </div>
    </>
  );
}

export default HelpPanel;
